/* eslint-disable jsx-a11y/media-has-caption */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Avatar } from '@material-ui/core';
import { MuLawDecoder, PcmAudioPlayer } from 'ui3';
import MjpegControls from './MjpegControls';
import './index.css';

const MjpegLivePlayer = ({ streamData }) => {
	const [videoBannerText, setVideoBannerText] = useState('Loading');
	const [showControls, setShowControls] = useState(false);
	const [isFullScreenIphone, setIsFullScreenIphone] = useState(false);
	const [showPortraitOverlay, setShowPortraitOverlay] = useState(true);
	const [error, setError] = useState('');
	const [isPlaying, setIsPlaying] = useState(false);
	
	const imgRef = useRef(null);
	const boxRef = useRef(null);
	const pcmPlayerRef = useRef(null);
	const audioAbortControllerRef = useRef(null);
	const abortControllerRef = useRef(null);
	const isPausedRef = useRef(false);
	const volumeRef = useRef(0.5);
	
	let hoverTimeout;

	// Initialize audio player if audio URL is provided
	useEffect(() => {
		if (streamData?.audio_url && !pcmPlayerRef.current) {
			const pcmPlayer = new PcmAudioPlayer();
			pcmPlayerRef.current = pcmPlayer;
			pcmPlayer.SetAudioVolumeFromSettings(volumeRef.current);
		}
	}, [streamData?.audio_url]);

	// Handle audio stream for G.711 uLaw
	const processAudioStream = useCallback(async (audioStreamUrl) => {
		if (!audioStreamUrl || !pcmPlayerRef.current) return;

		const audioAbortController = new AbortController();
		audioAbortControllerRef.current = audioAbortController;

		try {
			const response = await fetch(audioStreamUrl, {
				credentials: 'same-origin',
				signal: audioAbortController.signal
			});

			if (!response.ok) {
				console.error('Failed to fetch audio stream');
				return;
			}

			const reader = response.body.getReader();
			const muLawDecoder = new MuLawDecoder();

			const processAudioData = async () => {
				try {
					while (true) {
						const { done, value } = await reader.read();
						if (done) break;

						if (isPausedRef.current) {
							continue; // Skip processing if paused
						}

						// Process G.711 uLaw audio data
						const pcm16Bit = muLawDecoder.DecodeUint8ArrayToFloat32Array(value);
						pcmPlayerRef.current.AcceptBuffer([pcm16Bit], 8000); // 8kHz sample rate for G.711
					}
				} catch (error) {
					if (error.name !== 'AbortError') {
						console.error('Error processing audio stream:', error);
					}
				}
			};

			processAudioData();
		} catch (error) {
			if (error.name !== 'AbortError') {
				console.error('Error fetching audio stream:', error);
			}
		}
	}, []);

	const attachEventListeners = () => {
		const controlsContainer = boxRef.current;
		
		if (controlsContainer) {
			controlsContainer.addEventListener('mouseenter', () => {
				clearTimeout(hoverTimeout);
				setShowControls(true);
			});
			
			controlsContainer.addEventListener('mouseleave', () => {
				hoverTimeout = setTimeout(() => {
					setShowControls(false);
				}, 2500);
			});

			controlsContainer.addEventListener('touchstart', () => {
				clearTimeout(hoverTimeout);
				setShowControls(true);
			});
			
			controlsContainer.addEventListener('touchend', () => {
				hoverTimeout = setTimeout(() => {
					setShowControls(false);
				}, 2000);
			});
		}

		// For MJPEG streams, set live status immediately
		setVideoBannerText('Live');
		setIsPlaying(true);
		isPausedRef.current = false;
	};

	useEffect(() => {
		if (!streamData?.session_url) return;

		const abortController = new AbortController();
		abortControllerRef.current = abortController;

		// Clear any previous errors when starting new stream
		setError('');

		// Use img element for MJPEG streams
		const imgElement = imgRef.current;
		if (imgElement) {
			imgElement.src = streamData.session_url;
			imgElement.crossOrigin = 'anonymous';
			
			const handleImgError = (e) => {
				setError('Failed to load MJPEG stream');
			};
			
			const handleImgLoad = () => {
				setError(''); // Clear any previous errors
				setVideoBannerText('Live');
				setIsPlaying(true);
			};
			
			imgElement.addEventListener('load', handleImgLoad);
			imgElement.addEventListener('error', handleImgError);
			
			// Start audio stream if available
			if (streamData.audio_url) {
				processAudioStream(streamData.audio_url);
			}
			
			attachEventListeners();
			
			return () => {
				imgElement.removeEventListener('load', handleImgLoad);
				imgElement.removeEventListener('error', handleImgError);
				if (abortControllerRef.current) {
					abortControllerRef.current.abort();
				}
				if (audioAbortControllerRef.current) {
					audioAbortControllerRef.current.abort();
				}
				if (pcmPlayerRef.current) {
					pcmPlayerRef.current.Reset();
				}
				clearTimeout(hoverTimeout);
			};
		}
	}, [streamData?.session_url, streamData?.audio_url, processAudioStream]);

	const isLive = () => videoBannerText === 'Live';

	const handleIphoneFullScreenToggle = () => {
		setIsFullScreenIphone(!isFullScreenIphone);
	};

	const handleVolumeChange = (newVolume) => {
		volumeRef.current = newVolume;
		if (pcmPlayerRef.current) {
			pcmPlayerRef.current.SetAudioVolumeFromSettings(newVolume);
		}
	};

	const handlePlayPause = () => {
		if (streamData?.audio_url && pcmPlayerRef.current) {
			// For MJPEG with audio, toggle audio only
			if (isPlaying) {
				pcmPlayerRef.current.Pause();
				setIsPlaying(false);
				isPausedRef.current = true;
			} else {
				pcmPlayerRef.current.Resume();
				setIsPlaying(true);
				isPausedRef.current = false;
			}
		} else {
			// For MJPEG without audio, there's nothing to pause
			return;
		}
	};

	return (
		<div className="relative">
			<div
				className="mx-auto absolute top-0 font-bold flex items-center gap-8 text-white py-8 z-99 ml-10"
			>
				<Avatar src={streamData?.room.thumb} />
				<div>{streamData?.room.name}</div>
			</div>
			
			{error && <div className="text-red-500 p-4">{error}</div>}
			
			<div className="flex justify-center items-center aspect-ratio-16-9 relative">
				{!isFullScreenIphone ? (
					<div
						className="w-full video-player-box rounded-lg overflow-hidden h-0 relative"
						id={`video-container-${streamData?.room.id}`}
						ref={boxRef}
					>
						<img
							className="rounded-lg video-player object-fill w-full h-full"
							id={`mjpeg-img-${streamData?.room.id}`}
							ref={imgRef}
							alt="MJPEG Stream"
						/>

						{streamData?.overlay_image && streamData?.session_url ? (
							<img
								alt=""
								className="img-overlay-blur"
								height={boxRef.current?.clientHeight}
								id="overlayImage"
								src={streamData?.overlay_image}
								style={{
									position: 'absolute',
									top: 0,
									left: 0,
									zIndex: 50,
								}}
								width={boxRef.current?.clientWidth}
							/>
						) : null}
						
						{streamData?.session_url ? (
							<>
								<div className={`btn_live ${isLive() ? '' : 'not_btn_live'} z-50`} style={{ top: 14, right: 10 }}>
									{videoBannerText || 'Live'}
									<span className={`${isLive() ? 'live-icon' : 'not-live-icon'}`} />
								</div>
								<MjpegControls
									boxRef={boxRef}
									isPlaying={isPlaying}
									onPlayPause={handlePlayPause}
									onVolumeChange={handleVolumeChange}
									room={streamData}
									showControls={showControls}
								/>
							</>
						) : (
							<div className="stream-not-running-text">Live Streaming Is Not Available</div>
						)}
					</div>
				) : (
					// Fullscreen iPhone view
					<div
						className="flex items-center justify-center top-0 left-0 h-full w-full object-fill fixed z-999 bg-black"
						ref={boxRef}
					>
						{showPortraitOverlay ? (
							<div
								className="fixed sm:hidden h-screen w-screen z-999 flex items-center justify-center"
								style={{ background: 'rgba(0, 0, 0, .75)' }}
							>
								<button
									className="p-4 absolute top-72 right-16 text-white"
									onClick={() => setShowPortraitOverlay(false)}
									type="button"
								>
									×
								</button>
								<p className="text-white">For the best viewing experience, please rotate your device.</p>
							</div>
						) : null}
						<div className="relative w-full h-full flex items-center justify-center">
							<div className="w-full h-full relative bg-black">
								<img
									className="w-full h-full object-fill"
									id={`mjpeg-img-fullscreen-${streamData?.id}`}
									ref={imgRef}
									alt="MJPEG Stream"
								/>
								{streamData?.overlay_image ? (
									<img
										alt=""
										className="absolute top-0 left-0 z-99 w-full h-full object-fill"
										src={streamData?.overlay_image}
									/>
								) : null}
								<div className="flex items-center w-full h-48 absolute top-16 pl-16">
									<div className="absolute top-0 font-bold flex items-center gap-8 text-white z-99 ml-10">
										<Avatar src={streamData?.thumb} />
										<div>{streamData?.room.name}</div>
									</div>
									<div className={`btn_live ${isLive() ? '' : 'not_btn_live'} z-20`}>
										{videoBannerText || 'Live'}
										<span className={`${isLive() ? 'live-icon' : 'not-live-icon'}`} />
									</div>
								</div>
								<MjpegControls
									boxRef={boxRef}
									isPlaying={isPlaying}
									onPlayPause={handlePlayPause}
									onVolumeChange={handleVolumeChange}
									room={streamData}
									showControls={showControls}
								/>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default MjpegLivePlayer;